import { Suspense, useEffect, useState } from "react";
import lazyWithRetry from "../../utils/lazyWithRetry";
import logger from "../../utils/logger";
import { fetchSupportConfig } from "./supportChatApi";

/**
 * SupportWidgetGate. Decides whether the player gets the support chat.
 *
 * Asks the backend once per page load (GET /api/casino/support/config, driven
 * by the support_widget_enabled setting, flippable at runtime from admin
 * System Settings). On, the widget mounts. Off, nothing renders.
 *
 * That off state is a real kill switch, not a fallback: Botpress was retired
 * once this chat was proven, so there is no second widget to fall back to. If
 * the chat has to be turned off, players use support@cluck.us, which is in the
 * footer and reaches the same ticket queue.
 *
 * Fails CLOSED on an unreachable config call, since a broken read should not
 * put an unverified chat surface in front of players.
 */

const CONFIG_TIMEOUT_MS = 3000;

const SupportChatWidget = lazyWithRetry(() => import("./SupportChatWidget"));

export default function SupportWidgetGate() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);

    fetchSupportConfig(controller.signal)
      .then(({ widgetEnabled }) => {
        if (alive) setEnabled(widgetEnabled);
      })
      .catch((err) => {
        if (!alive) return;
        logger.warn("[Support] Widget config unavailable, staying hidden", {
          message: err?.message,
        });
        setEnabled(false);
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      alive = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  if (!enabled) return null;

  return (
    <Suspense fallback={null}>
      <SupportChatWidget />
    </Suspense>
  );
}
